/* eslint-disable import/prefer-default-export */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { createProductDraftFromMaster } from './createProductDraftFromMaster';
import { createVariantDraftFromMaster } from './createVariantDraftFromMaster';
import { getDraftSyncGroups, getOverwriteVariantIds } from './helpers/listingSyncHelpers';
import { showDraftForChannelToast } from './helpers/showDraftForChannelToast';

/**
 * Orchestration-only thunk: no own reducer, SDK calls, or Toast.
 * Same as syncProductToDraft, but scoped to selected masterVariantIds.
 */
export const syncSelectedVariantsToDraft = createAsyncThunk(
  'products/syncSelectedVariantsToDraft',
  async ({
    productId,
    channelIds,
    masterVariantIds,
    draftMedia = {},
    overrideExisting = false,
  }, { dispatch, getState, extra: { Toast } }) => {
    const productsState = getState().products;
    const product = productsState.items.find(({ _id }) => _id === productId);
    const groups = getDraftSyncGroups({ product, productsState, channelIds, masterVariantIds });

    if (groups.channelsWithoutListing.length) {
      await dispatch(createProductDraftFromMaster({
        productId,
        channelIds: groups.channelsWithoutListing,
        masterVariantIds,
        draftMedia,
        overrideExisting,
        suppressToast: true,
      })).unwrap();
    }

    const syncExistingListingChannel = async ({
      channelId,
      existingDraftVariantIds,
      existingLiveVariantIds,
      missingVariantIds,
      liveOnlyVariantIds = [],
    }) => {
      if (missingVariantIds.length) {
        await dispatch(createVariantDraftFromMaster({
          productId,
          channelIds: [channelId],
          masterVariantIds: missingVariantIds,
          draftMedia,
          overrideExisting,
          suppressToast: true,
        })).unwrap();
      }

      const overwriteVariantIds = getOverwriteVariantIds({
        existingDraftVariantIds,
        existingLiveVariantIds,
        liveOnlyVariantIds,
      });

      if (!overwriteVariantIds.length) return;

      await dispatch(createProductDraftFromMaster({
        productId,
        channelIds: [channelId],
        masterVariantIds: overwriteVariantIds,
        draftMedia,
        overrideExisting,
        suppressToast: true,
      })).unwrap();
    };

    await Promise.all([
      ...groups.publishedListingChannels.map(syncExistingListingChannel),
      ...groups.draftListingChannels.map(syncExistingListingChannel),
    ]);

    const { user: { team } } = getState();
    showDraftForChannelToast({
      Toast,
      channelIds,
      salesChannels: team?.salesChannels || [],
    });

    return { productId, channelIds, masterVariantIds };
  },
);
